import { labelWithSpecies } from 'common/utils/species'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useMedia from 'common/hooks/useMedia'
import { useCurrentCycleInfo, useSpeciesName } from 'modules/pond/api/selector'
import { useGetShrimpTopupSummary } from 'modules/shrimpTopup/api/queries'
import { shrimpTopupDateTableFilterState } from 'modules/shrimpTopup/stores/atom'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'

import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Card, Grid, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import ShrimpTopupRecordDialog from '../ShrimpTopupRecordDialog'

interface ShrimpTopupRecordCardProps {
  children: React.ReactNode
}

const ShrimpTopupRecordCard: FC<ShrimpTopupRecordCardProps> = ({ children }) => {
  const { t } = useTranslation('shrimpTopup')
  const species = useSpeciesName()
  const isMobile = useMedia('sm');
  const showRecordBtn = usePermitToActionFeature([CRUDAction.Create], FeatureKey.ShrimpTopup)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [date, setDate] = useRecoilState(shrimpTopupDateTableFilterState)
  const { data: summaryData } = useGetShrimpTopupSummary()

  const cycleInfo = useCurrentCycleInfo()
  const isCompleted = cycleInfo?.status === 'completed'

  const handleOpen = () => {
    setDialogOpen(true)
  }

  const handleChangeDate = (newDate: Date | null) => {
    setDate(newDate ?? undefined)
  }

  useEffect(() => {
    return () => {
      setDate(undefined)
    }
  }, [setDate])

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <Grid container spacing={2} alignItems="center" mb={2}>
        {isMobile ? (
          <>
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  backgroundColor: '#CFFFE0',
                  px: 3,
                  py: 1.5,
                  my: 1,
                }}
              >
                <Typography variant="h6" sx={{ color: '#009A4D', fontWeight: 600 }}>
                  {t('topup.totalRelease', { species })}
                </Typography>
                <Typography variant="h6" sx={{ color: '#009A4D', fontWeight: 600 }}>
                  {Number(summaryData?.totalAmount ?? 0).toLocaleString()}{' '}
                  <span style={{ fontSize: 16, fontWeight: 400 }}>{t('topup.unit')}</span>
                </Typography>
              </Box>
            </Grid>
            {showRecordBtn && !isCompleted && (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleOpen}
                  startIcon={<AddIcon />}
                >
                  {labelWithSpecies(t('topup.record'), species)}
                </Button>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                <Typography variant="h5">{labelWithSpecies(t('topup.history'), species)}</Typography>
                <DatePicker
                  label={t('topup.dateSearch')}
                  value={date}
                  format="dd/MM/yyyy"
                  onChange={handleChangeDate}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['clear', 'accept'] },
                  }}
                />
              </Box>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={5}>
              <Typography variant="h5">{labelWithSpecies(t('topup.history'), species)}</Typography>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <DatePicker
                  label={t('topup.dateSearch')}
                  value={date}
                  format="dd/MM/yyyy"
                  onChange={handleChangeDate}
                  slotProps={{
                    textField: { size: 'small' },
                    actionBar: { actions: ['clear', 'accept'] },
                  }}
                />
                {showRecordBtn && !isCompleted && (
                  <Button variant="contained" onClick={handleOpen} startIcon={<AddIcon />}>
                    {labelWithSpecies(t('topup.record'), species)}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  backgroundColor: '#CFFFE0',
                  borderRadius: 2,
                  px: 3,
                  py: 1.5,
                  my: 1,
                }}
              >
                <Typography variant="h6" sx={{ color: '#009A4D', fontWeight: 600 }}>
                  {t('topup.totalRelease')}
                </Typography>
                <Typography variant="h6" sx={{ color: '#009A4D', fontWeight: 600 }}>
                  {Number(summaryData?.totalAmount ?? 0).toLocaleString()}{' '}
                  <span style={{ fontSize: 16, fontWeight: 400 }}>{t('topup.unit')}</span>
                </Typography>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      {children}
      <ShrimpTopupRecordDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Card>
  )
}

export default ShrimpTopupRecordCard
